<script lang="ts" setup>
import { onMounted } from "vue";
import { vIntersectionObserver } from "@vueuse/components";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VWrapperLocal from "~/components/VWrapperLocal.vue";
import { useContentStore } from "~/store/content";
import VCatalogHeaderLabel from "~/containers/VCatalog/VCatalogHeaderLabel.vue";

const SLIDER_VISIBILITY_CALCULATION_DEBOUNCE = 500;

interface ICatalogCategoriesSliderProps {
  currentCategoryTreeName?: string;
  promo?: boolean;
}

const contentStore = useContentStore();
const { send } = useAnalytics();

const props = defineProps<ICatalogCategoriesSliderProps>();

const currentlyVisibleSlides = ref<Record<number, boolean>>({});
const listenForSliderAnalytics = ref(false);

const onItemClick = (title: string, position: number) => {
  send(
    props.promo
      ? "PromoCatalog:Navigation:Slider:Item:Click"
      : "Catalog:Navigation:Slider:Item:Click",
    {
      category_name: props.currentCategoryTreeName || "",
      banner_name: title,
      position: position + 1,
    },
  );
};
const onItemVisibility = (
  title: string,
  position: number,
  [{ isIntersecting }]: IntersectionObserverEntry[],
) => {
  currentlyVisibleSlides.value[position] = isIntersecting;
  if (isIntersecting) {
    send(
      props.promo
        ? "PromoCatalog:Navigation:Slider:Item:View"
        : "Catalog:Navigation:Slider:Item:View",
      {
        category_name: props.currentCategoryTreeName || "",
        banner_name: title,
        position: position + 1,
      },
    );
  }
};

onMounted(() => {
  send(
    props.promo
      ? "PromoCatalog:Navigation:Slider:View"
      : "Catalog:Navigation:Slider:View",
  );
});
const onSlideChange = () => {
  if (listenForSliderAnalytics.value) {
    setTimeout(() => {
      send(
        props.promo
          ? "PromoCatalog:Navigation:Slider:Scroll"
          : "Catalog:Navigation:Slider:Scroll",
        {
          items_count: contentStore.catalog?.items.length || 0,
          number: Object.entries(currentlyVisibleSlides.value)
            .filter((entry) => entry[1])
            .map((item) => parseInt(item[0]) + 1)
            .join(";"),
        },
      );
    }, SLIDER_VISIBILITY_CALCULATION_DEBOUNCE);
  } else {
    listenForSliderAnalytics.value = true;
  }
};
</script>

<template>
  <VWrapperLocal class="catalog-categories-slider__wrapper">
    <AppSlider
      v-if="contentStore.catalog?.items?.length"
      class="catalog-categories-slider"
      :breakpoints="{
        xs: {
          slidesPerView: 'auto',
          spaceBetween: 8,
          slidesOffsetAfter: 12,
        },
        s: {
          slidesPerView: 'auto',
          spaceBetween: 12,
          slidesOffsetAfter: 20,
        },
        m: {
          slidesPerView: 'auto',
          spaceBetween: 20,
          slidesOffsetAfter: 32,
        },
        ml: {
          slidesPerView: 'auto',
          spaceBetween: 20,
          slidesOffsetAfter: 32,
        },
        l: {
          slidesPerView: 4,
          spaceBetween: 24,
          slidesOffsetAfter: 0,
          slidesOffsetBefore: 0,
          navigation: true,
        },
        xl: {
          slidesPerView: 6,
          spaceBetween: 24,
          slidesOffsetAfter: 0,
          slidesOffsetBefore: 0,
          navigation: true,
        },
      }"
      @slide-change="onSlideChange"
    >
      <AppSliderSlide
        v-for="(item, index) in contentStore.catalog?.items"
        :key="item.position"
        v-intersection-observer="
          (io) => onItemVisibility(item.title, index, io)
        "
        :size="{ xs: 6, m: 4, ml: 3, l: 'auto' }"
        class="catalog-categories-slider__item-wrapper"
      >
        <NuxtLink
          class="catalog-categories-slider__item pl-hover-base"
          :to="item.offerURL"
          @click="onItemClick(item.title, index)"
        >
          <VText
            :font="{
              xs: 'caption-large-accent-low',
              l: 'body-large-accent-low',
            }"
            class="catalog-categories-slider__item-text"
            :class="{
              'catalog-categories-slider__item-text_white':
                item.titleType === 2,
            }"
          >{{ item.title }}
          </VText>

          <div class="catalog-categories-slider__item-img">
            <img :src="item.publicImageURL" alt="" loading="lazy">
          </div>

          <VCatalogHeaderLabel
            v-if="item.labelType !== 1"
            :text="item.labelText"
            :label-type="item.labelType"
            class="catalog-categories-slider__item-badge"
          />
        </NuxtLink>
      </AppSliderSlide>
    </AppSlider>
  </VWrapperLocal>
</template>

<style lang="postcss" scoped>
.catalog-categories-slider {
  &__wrapper {
    margin-top: var(--pl-unit-x4);
    margin-bottom: var(--pl-unit-x4);
  }

  &__item {
    background-color: #f8e3ff;
    display: flex;
    flex-direction: column;
    aspect-ratio: 1 / 1;
    position: relative;
    overflow: hidden;
    border-radius: 16px;
  }

  &__item-text {
    padding: 16px;
    z-index: 2;

    &_white {
      color: var(--pl-text-invert-primary);
    }
  }

  &__item-img {
    width: 100%;
    height: 100%;
    position: absolute;

    img {
      width: 100%;
      height: 100%;
      position: absolute;
      object-fit: cover;
      object-position: center;
    }
  }

  &__item-badge {
    position: absolute;
    bottom: 0;
    left: 0;
  }
}

@media (--pl-viewport-from-l) {
  .catalog-categories-slider {
    &__wrapper {
      margin-top: var(--pl-unit-x6);
      margin-bottom: var(--pl-unit-x6);
    }

    &__item {
      border-radius: 24px;
    }
  }
}

@media (--pl-viewport-from-xl) {
  .catalog-categories-slider {
    &__wrapper {
      margin-top: var(--pl-unit-x8);
      margin-bottom: 56px;
    }
  }
}
</style>
